import React, { useRef } from "react";
import { useFormikContext } from "formik";
import { KTSVG, toAbsoluteUrl } from "../../../../../../_metronic/helpers";
import { GalleryImagePreview } from "./GalleryImagePreview";
import { showErrorAlert } from "../../../../../helpers/alert";
import { useParams } from "react-router-dom";
import { deleteExperienceImage } from "../../../../../services/teamExperienceService";
import { useConfirmAction } from "../../../../../hooks/utils/useConfirmAction";
import { getPersistedImageId } from "./utils";

export default function GalleryUploadField() {
  const { values, setFieldValue } = useFormikContext();
  const { id } = useParams<{ id: string }>();
  const confirm = useConfirmAction();
  const inputRef = useRef(null);
  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);

  const handleDragStart = (e: React.DragEvent, position: number) => {
    dragItem.current = position;
  };

  const handleDragOver = (e: React.DragEvent, position: number) => {
    e.preventDefault();
    dragOverItem.current = position;
  };

  const handleDragEnd = () => {
    if (dragItem.current !== null && dragOverItem.current !== null && dragItem.current !== dragOverItem.current) {
      const copyListItems = [...(values.galleryImages || [])];
      const dragItemContent = copyListItems[dragItem.current];
      copyListItems.splice(dragItem.current, 1);
      copyListItems.splice(dragOverItem.current, 0, dragItemContent);
      dragItem.current = null;
      dragOverItem.current = null;
      setFieldValue("galleryImages", copyListItems);
    }
  };

  const handleClick = () => {
    inputRef.current.click();
  };

  const handleFiles = (files: FileList | File[]) => {
    if (!files) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    const allowedExtensions = [".jpg", ".jpeg", ".png", ".webp"];

    const arr = Array.from(files);

    const validFiles = arr.filter((file) => {
      const ext = file.name.toLowerCase().slice(file.name.lastIndexOf("."));
      return (
        allowedTypes.includes(file.type) &&
        allowedExtensions.includes(ext)
      );
    });

    if (validFiles.length === 0) {
      showErrorAlert({title:'Unsupported format',message:'Only JPG, JPEG, PNG, or WEBP allowed.'});
      return;
    }

    setFieldValue("galleryImages", [
      ...(values.galleryImages || []),
      ...validFiles,
    ]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const removeImage = async (index, img) => {
    // UAT-217: validate the actual id VALUE, not the presence of the `id` key — the edit path
    // always maps images to { id, image }, so id-less drafts still carry an `id` key (undefined).
    const persistedId = getPersistedImageId(img);
    const accept = await confirm({
      title: 'Delete Image',
      message: 'Are you sure you want to remove this image from the gallery?',
    });
    if (!accept) return;

    if (persistedId !== null && id) {
      try {
        await deleteExperienceImage(Number(id), persistedId);
      } catch (error) {
        return;
      }
    }
    const updated = values.galleryImages.filter((_, i) => i !== index);
    setFieldValue("galleryImages", updated);
  };

  return (
    <div className="w-100" onDragOver={(e) => e.preventDefault()} onDrop={handleDrop}>
      <div className={`tap-add-experience-2-upload-area tap-flex-center ${(values.galleryImages?.length > 0) ? 'd-none' : 'd-flex'}`} onClick={handleClick}>
        <div className="tap-flex-center flex-column">
          <div className="img-upload">
            <KTSVG className="img-upload" path={toAbsoluteUrl('/media/svg/nobilis/teams_and_partner/upload_icon.svg')} />
          </div>
          <div className="description">Drag your photo here or upload</div>
          <div className="btn_upload">UPLOAD IMAGE</div>
        </div>
      </div>
      {/* LISTA DE IMÁGENES */}
      <div className={`tap-add-experience-2-gallery-upload-container ${(values.galleryImages?.length > 0) ? 'd-flex' : 'd-none'}`}>
        <div className="tap-add-experience-2-gallery-upload-grid">
          {values.galleryImages?.map((img, index) => {
            const isSaved = img && typeof img === 'object' && 'image' in img;
            const src = isSaved
                ? img.image
                : (typeof img === 'string' ? img : URL.createObjectURL(img));

            return (
              <div 
                key={index}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                style={{ cursor: 'grab' }}
              >
                <GalleryImagePreview 
                  src={src} 
                  file={img} 
                  onRemove={() => removeImage(index, img)} 
                  showDelete={true}
                />
              </div>
            );
          })}


          {/* BOTÓN DE SUBIR */}
          <div className="w-100 cursor-pointer">
            <div className="tap-add-experience-2-label-gallery-upload" onClick={handleClick}>upload more image</div>
          </div>
        </div>
      </div>

      {/* INPUT OCULTO */}
      <input type="file" multiple accept=".jpg,.jpeg,.png,.webp" style={{ display: "none" }} ref={inputRef} onChange={(e) => handleFiles(e.target.files)}/>
    </div>
  );
}
